/**
 * Retrieves authentication tokens from local storage.
 * It prioritizes 'kk_token' and falls back to 'token' for legacy compatibility.
 * It handles both raw token strings and JSON objects.
 * @returns {object|null} An object with an 'access' key, or null if no token is found.
 */
/**
 * Returns true if this browser tab is running an admin-initiated
 * "login as user" impersonation session (flagged via sessionStorage).
 */
export const isImpersonating = () => {
    try {
        return sessionStorage.getItem('kk_impersonating') === '1';
    } catch (e) {
        return false;
    }
};

/**
 * Storage abstraction that transparently uses sessionStorage while
 * impersonating (so the popout session never touches/overwrites the
 * admin's own localStorage-backed session), and localStorage otherwise.
 */
export const authStorage = {
    getItem: (key) => (isImpersonating() ? sessionStorage.getItem(key) : localStorage.getItem(key)),
    setItem: (key, value) => (isImpersonating() ? sessionStorage.setItem(key, value) : localStorage.setItem(key, value)),
    removeItem: (key) => (isImpersonating() ? sessionStorage.removeItem(key) : localStorage.removeItem(key)),
};

export const getAuthTokens = () => {
    // Prioritize 'kk_token', then fall back to 'token' or 'authTokens' for compatibility.
    const tokenData = authStorage.getItem('kk_token') || authStorage.getItem('token') || authStorage.getItem('authTokens');

    if (!tokenData) {
        return null;
    }

    try {
        // Attempt to parse as JSON (for objects like { access: '...', refresh: '...' })
        const parsed = JSON.parse(tokenData);
        if (parsed && typeof parsed === 'object' && parsed.access) {
            return parsed; // It's the new format, return as is.
        }
    } catch (e) {
        // If parsing fails, it's a raw token string.
        // Wrap it in the expected object structure.
        return { access: tokenData };
    }
    
    // This case handles if the stored value is a JSON but not in the expected format.
    // We assume it's a raw token if it's not a valid JSON object with an access key.
    return { access: tokenData };
};

/**
 * Constructs the authorization and content-type headers for API requests.
 * @param {boolean} isFormData - Set to true if the request body is FormData.
 *                               This omits the 'Content-Type' header, allowing
 *                               the browser to set it automatically with the
 *                               correct boundary for file uploads.
 * @returns {HeadersInit} The headers object for the fetch request.
 */
export const getAuthHeaders = (isFormData = false) => {
    const tokens = getAuthTokens();
    const headers = {};

    if (tokens?.access) {
        headers['Authorization'] = `Bearer ${tokens.access}`;
    }

    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }

    return headers;
};